/**
 * 房间层的消息路由决策（**纯函数**）。
 *
 * 从 `useRoomSession` 里抽出来，是因为「谁的话该转发给谁」「观众发来的走子要不要丢」
 * 「这一步是谁走的」全是纯逻辑。抽出来之后，多端（房主 + 玩家 + 观众）联动的集成测试
 * 就能在没有 DOM 的 CI 里跑 —— 本仓库没有 jsdom，React hook 层渲染不了。
 *
 * 本模块只**决策**，不产生任何副作用：调用方负责派发 `actions`、转发 `relay`、入队 `chat`。
 */

import type { Pos } from '@games/core'
import type { GameMessage } from '@games/protocol'
import type { RoomAction, RoomState } from './roomReducer'

export interface RoutingResult<M> {
    /** 要派发给 reducer 的动作 */
    readonly actions: readonly RoomAction<M>[]
    /** 房主需要转发给「除发送者外全部连接」的消息 */
    readonly relay?: GameMessage
    /** 要写进聊天记录的一条消息 */
    readonly chat?: { readonly text: string; readonly name?: string }
}

/**
 * 房主权威署名：按 peer 查身份表。
 *
 * 房主自己发出的消息不走这里（`sendChat` 直接带上自己的名字），
 * 所以身份表里查不到就说明对方不是本房间的人 → 不给署名。
 */
export function nameForPeer<S, M>(state: RoomState<S, M>, peer: string): string | undefined {
    return state.identities.find((item) => item.peer === peer)?.name
}

/**
 * 规划一条入站消息的处理方案。
 *
 * 传输层不认识角色，所以「房主中继」「观众不能走子」这些规则都落在这一层。
 */
export function planMessage<S, M>(
    state: RoomState<S, M>,
    from: string,
    message: GameMessage,
    toMove: (from: Pos, to: Pos) => M,
): RoutingResult<M> {
    const isHost = state.role === 'host'
    const actions: RoomAction<M>[] = []

    switch (message.type) {
        case 'hello':
            // 只有房主做接入判定
            if (isHost) actions.push({ type: 'hello', token: message.token, peer: from })
            break

        case 'welcome':
            if (!isHost) {
                actions.push({
                    type: 'welcome',
                    role: message.role,
                    seat: message.seat ?? null,
                    name: message.name,
                })
            }
            break

        case 'sync-request':
            if (isHost) actions.push({ type: 'sync-request', peer: from })
            break

        case 'sync':
            // 只有接入方需要补局，房主自己就是权威
            if (!isHost) {
                actions.push({
                    type: 'sync',
                    moves: message.moves.map((entry) => ({
                        by: entry.by,
                        move: toMove(entry.from, entry.to),
                    })),
                })
            }
            break

        case 'move': {
            const move = toMove(message.from, message.to)
            if (!isHost) {
                // 观众只能靠中继附带的 by 才知道执子方
                actions.push({ type: 'remote-move', move, by: message.by })
                break
            }
            const identity = state.identities.find((item) => item.peer === from)
            // 观众不能走子：丢弃，而且**不转发**（D13）
            if (identity && identity.role === 'spectator') break
            actions.push({ type: 'remote-move', move })
            return {
                actions,
                // 执子方由房主判定，不信任对方自己填的值
                relay: {
                    type: 'move',
                    from: message.from,
                    to: message.to,
                    by: state.opponent ?? undefined,
                },
            }
        }

        case 'chat': {
            const name = isHost ? nameForPeer(state, from) : message.name
            return {
                actions,
                chat: { text: message.text, name },
                ...(isHost
                    ? { relay: { type: 'chat' as const, text: message.text, name } }
                    : {}),
            }
        }

        case 'restart':
            actions.push({ type: 'restart', source: 'peer' })
            if (isHost) return { actions, relay: message }
            break

        case 'peer-state':
            // 观众也收这条：它重放出的指纹应当与权威一致（校验在调用方）
            if (isHost) return { actions, relay: message }
            break

        case 'audience':
            // 房主是权威（人数从身份表推导），不收听；其他人直接采用房主报的数
            if (!isHost) actions.push({ type: 'audience', count: message.count })
            break

        case 'room-full':
            actions.push({ type: 'abort', reason: '房间已经有人在对局中' })
            break
    }

    return { actions }
}
